/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\LanguageSettingsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import { useLanguageOptions } from '../../../hooks/useLanguageOptions';
import type { LanguageSettings } from '../types';
import { convertSoundPathToSelectorValue, convertSelectorValueToSoundPath } from '../languageUtils';

interface SoundOption {
  value: string;
  label: string;
}

interface LanguageSettingsSectionProps {
  settings: LanguageSettings;
  soundOptions: SoundOption[];
  languageOptions: ReturnType<typeof useLanguageOptions>['languageOptions'];
  onSettingChange: <K extends keyof LanguageSettings>(field: K, value: LanguageSettings[K]) => void;
  t: (key: string) => string;
}

export function LanguageSettingsSection({
  settings, soundOptions, languageOptions, onSettingChange, t,
}: LanguageSettingsSectionProps) {
  return (
    <FormBox title={t('languages.sections.settings')} icon={<Settings className="h-5 w-5" />}>
      <div className="grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">

        {/* Sound Prefix */}
        <Label htmlFor="soundPrefix">
          <SimpleTooltip content={t('languages.settings.soundPrefix.tooltip')}>
            {t('languages.settings.soundPrefix.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <select
            id="soundPrefix"
            value={convertSoundPathToSelectorValue(settings.sound_prefix)}
            onChange={(e) => onSettingChange('sound_prefix', convertSelectorValueToSoundPath(e.target.value, languageOptions))}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">{t('languages.settings.soundPrefix.placeholder')}</option>
            {soundOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Fallback Sound Prefix */}
        <Label htmlFor="fallbackSoundPrefix">
          <SimpleTooltip content={t('languages.settings.fallbackSoundPrefix.tooltip')}>
            {t('languages.settings.fallbackSoundPrefix.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <select
            id="fallbackSoundPrefix"
            value={convertSoundPathToSelectorValue(settings.fallback_sound_prefix)}
            onChange={(e) => onSettingChange('fallback_sound_prefix', convertSelectorValueToSoundPath(e.target.value, languageOptions))}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">{t('languages.settings.fallbackSoundPrefix.placeholder')}</option>
            {soundOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Auto Detect */}
        <Label htmlFor="autoDetect">
          <SimpleTooltip content={t('languages.settings.autoDetect.tooltip')}>
            {t('languages.settings.autoDetect.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={settings.auto_detect} onChange={(v) => onSettingChange('auto_detect', v)} />
        </div>

        {/* Detect Source */}
        <Label htmlFor="detectSource">
          <SimpleTooltip content={t('languages.settings.detectSource.tooltip')}>
            {t('languages.settings.detectSource.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <select
            id="detectSource"
            value={settings.detect_source}
            onChange={(e) => onSettingChange('detect_source', e.target.value as LanguageSettings['detect_source'])}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="none">{t('languages.settings.detectSource.none')}</option>
            <option value="sip_accept_language">{t('languages.settings.detectSource.sipAcceptLanguage')}</option>
            <option value="caller_id">{t('languages.settings.detectSource.callerId')}</option>
            <option value="geoip">{t('languages.settings.detectSource.geoip')}</option>
          </select>
        </div>

        {/* Session Only */}
        <Label htmlFor="sessionOnly">
          <SimpleTooltip content={t('languages.settings.sessionOnly.tooltip')}>
            {t('languages.settings.sessionOnly.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={settings.session_only} onChange={(v) => onSettingChange('session_only', v)} />
        </div>

        {/* Revert After Transfer */}
        <Label htmlFor="revertAfterTransfer">
          <SimpleTooltip content={t('languages.settings.revertAfterTransfer.tooltip')}>
            {t('languages.settings.revertAfterTransfer.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={settings.revert_after_transfer} onChange={(v) => onSettingChange('revert_after_transfer', v)} />
        </div>

        {/* Apply Channel Vars */}
        <Label htmlFor="applyChannelVars">
          <SimpleTooltip content={t('languages.settings.applyChannelVars.tooltip')}>
            {t('languages.settings.applyChannelVars.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={settings.apply_channel_vars} onChange={(v) => onSettingChange('apply_channel_vars', v)} />
        </div>

      </div>
    </FormBox>
  );
}
